import React from "react";
import { ChevronRight, Handshake } from "lucide-react";
import MarkThumbnail from "./MarkThumbnail";
import GoodsAndServices from "./GoodsAndServices";
import { StatusBadge } from "../ui/Badge";
import {
  EM_DASH,
  SIMILARITY_TEXT_CLASS,
  formatDate,
  formatOwners,
  officeLabel,
  similarityTone,
} from "../../lib/format";

/**
 * List view: one wide row per mark. The mark and what it covers (wordmark,
 * serial, goods & services, owner) read down the left; where it stands
 * (status, class, dates, register) sits on the right.
 *
 * Sits between the grid and the table — more context per mark than the
 * table, far more marks per screen than the grid.
 */
function Row({ label, children }) {
  return (
    <>
      <dt className="text-sm font-semibold text-ink-900">{label}</dt>
      <dd className="min-w-0 text-sm text-ink-700">{children}</dd>
    </>
  );
}

export default function ResultsList({
  rows,
  selected,
  onToggleRow,
  similarityMap,
  onOpenImage,
  onOpenDetail,
  onFindLicensing,
  isDetailLoading = () => false,
}) {
  return (
    <div className="space-y-3 border-x border-ink-200 bg-paper/60 p-4">
      {rows.map((row, index) => {
        const isSelected = selected.has(row.trademarkId);
        const score = similarityMap.get(row.trademarkId);
        const owners = formatOwners(row);

        return (
          <article
            key={row.trademarkId || index}
            onClick={() => onOpenDetail?.(row)}
            tabIndex={onOpenDetail ? 0 : undefined}
            role={onOpenDetail ? "button" : undefined}
            onKeyDown={(e) => {
              if (
                onOpenDetail &&
                e.target === e.currentTarget &&
                (e.key === "Enter" || e.key === " ")
              ) {
                e.preventDefault();
                onOpenDetail(row);
              }
            }}
            className={`flex items-start gap-4 rounded border p-4 transition-shadow ${
              onOpenDetail ? "cursor-pointer hover:shadow-float" : ""
            } ${isSelected ? "border-[#8f5eeb]-400 bg-[#8f5eeb]-100/50" : "border-ink-200 bg-white"}`}
          >
            <input
              type="checkbox"
              checked={isSelected}
              onChange={() => onToggleRow(row.trademarkId)}
              onClick={(e) => e.stopPropagation()}
              aria-label={`Select ${row.wordMark || "this record"}`}
              className="mt-1 h-4 w-4 shrink-0 cursor-pointer rounded border-ink-300 text-ink-900 focus:ring-seal-500/40"
            />

            <div className="shrink-0" onClick={(e) => e.stopPropagation()}>
              <MarkThumbnail
                wordMark={row.wordMark}
                imageUrl={row.imageUrl}
                size="lg"
                onClick={() =>
                  onOpenImage({ url: row.imageUrl, wordMark: row.wordMark })
                }
              />
            </div>

            <div className="grid min-w-0 flex-1 gap-x-8 gap-y-3 lg:grid-cols-[minmax(0,3fr)_minmax(0,2fr)]">
              <dl className="grid grid-cols-[104px_minmax(0,1fr)] gap-x-3 gap-y-2">
                <Row label="Wordmark">
                  <span className="break-words font-semibold text-[#8f5eeb]-600 underline underline-offset-2">
                    {row.wordMark || EM_DASH}
                  </span>
                </Row>
                <Row label="Serial">
                  <span className="font-mono text-xs">
                    {row.applicationNumber || EM_DASH}
                  </span>
                </Row>
                <Row label="Goods & services">
                  <GoodsAndServices
                    items={row.goodsAndServices}
                    loading={isDetailLoading(row)}
                    lines={2}
                    expandable
                  />
                </Row>
                <Row label="Owners">
                  {owners ? (
                    <span className="line-clamp-2 break-words" title={owners}>
                      {owners}
                    </span>
                  ) : (
                    <span className="text-ink-300">
                      {isDetailLoading(row) ? "Loading…" : "Not published"}
                    </span>
                  )}
                </Row>
              </dl>

              <dl className="grid grid-cols-[84px_minmax(0,1fr)] content-start gap-x-3 gap-y-2">
                <Row label="Status">
                  <StatusBadge status={row.status} />
                </Row>
                <Row label="Class">
                  {row.niceClasses?.length
                    ? row.niceClasses.join(", ")
                    : EM_DASH}
                </Row>
                <Row label="Filed">{formatDate(row.filingDate)}</Row>
                <Row label="Register">{officeLabel(row.jurisdiction)}</Row>
                {score !== undefined && (
                  <Row label="Similarity">
                    <span
                      className={`text-xs font-bold ${SIMILARITY_TEXT_CLASS[similarityTone(score)]}`}
                    >
                      {score}%
                    </span>
                  </Row>
                )}
              </dl>
            </div>

            <div
              className="flex shrink-0 items-start gap-1"
              onClick={(e) => e.stopPropagation()}
            >
              {onFindLicensing && (
                <button
                  type="button"
                  onClick={() => onFindLicensing(row)}
                  title="Find potential licensing"
                  aria-label={`Find potential licensing for ${row.wordMark || "this mark"}`}
                  className="rounded p-1.5 text-ink-400 hover:bg-[#8f5eeb]-100 hover:text-[#8f5eeb]-600"
                >
                  <Handshake className="h-4 w-4" aria-hidden="true" />
                </button>
              )}
              <ChevronRight
                className="mt-1.5 hidden h-5 w-5 shrink-0 text-ink-300 sm:block"
                aria-hidden="true"
              />
            </div>
          </article>
        );
      })}
    </div>
  );
}
